/**
 * titleSideImage.ts — слово владельца 21.10.2026 (задание
 * stylepack/title-side-image): КАРТИНКА СЛЕВА ОТ ТЕКСТА заголовка и
 * подзаголовка в одной строке (Moonly: круглый аватар 60, через 15 точек
 * текст вопроса, середины совпадают).
 *
 * Границы, умолчания, значения выравнивания, перечень полей и типы — ЗЕРКАЛО
 * сервера (backend/src/ai-funnels/block-schema/title-side-image.ts); разъезд
 * ловит сторож `title-side-image.ends.spec.ts` — он читает ЭТОТ файл с
 * диска, форму `{ min: N, max: N }` и списков не менять, не обновив его.
 *
 * Разметку собирает доля ядра `core-blocks/TitleSideImageRenderer.tsx`:
 * внутри САМОГО текстового тега — строчный ряд `inline-flex` [картинка]
 * [текст]. Тег сохраняет весь свой стиль, поэтому выравнивание блока двигает
 * пару целиком (по центру картинка остаётся слева от текста), а кегль,
 * цвет, жирность и отступы блока действуют как прежде.
 *
 * Адрес — общий фильтр адресов (`safeHref`, та же политика схем, что у
 * сервера): негодный или пустой — картинки нет, разметка прежняя.
 */
import type { CSSProperties } from 'react';
import { safeHref } from '@/utils/isAllowedUrl';

/** Типы с картинкой слева от текста — зеркало сервера. */
export const TITLE_SIDE_IMAGE_BLOCK_TYPES: ReadonlySet<string> = new Set([
  'title',
  'sub_title',
]);

/** Поля картинки — зеркало `titleSideImageShape` сервера. */
export const TITLE_SIDE_IMAGE_FIELDS = [
  'sideImageUrl',
  'sideImageSize',
  'sideImageRadius',
  'sideImageGap',
  'sideImageAlign',
] as const;

export const TITLE_SIDE_IMAGE_BOUNDS = {
  sideImageSize: { min: 12, max: 120 },
  sideImageRadius: { min: 0, max: 60 },
  sideImageGap: { min: 0, max: 40 },
} as const;

export const TITLE_SIDE_IMAGE_DEFAULTS = {
  sideImageSize: 48,
  sideImageGap: 12,
} as const;

export const TITLE_SIDE_IMAGE_ALIGNS = ['center', 'top'] as const;

export type TitleSideImageAlign = (typeof TITLE_SIDE_IMAGE_ALIGNS)[number];

export interface TitleSideImageFields {
  sideImageUrl?: string;
  sideImageSize?: number;
  sideImageRadius?: number;
  sideImageGap?: number;
  sideImageAlign?: TitleSideImageAlign;
}

export interface TitleSideImageParts {
  src: string;
  row: CSSProperties;
  img: CSSProperties;
  text: CSSProperties;
}

/** Число в границах; не число — `undefined` (берётся умолчание). */
function clampPx(
  value: unknown,
  bounds: { min: number; max: number },
): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  return Math.min(bounds.max, Math.max(bounds.min, value));
}

/** Части строки; `null` — картинки нет (нет годного адреса). */
export function titleSideImageParts(
  fields: TitleSideImageFields,
): TitleSideImageParts | null {
  const src = safeHref(fields.sideImageUrl, '');
  if (!src) return null;
  const B = TITLE_SIDE_IMAGE_BOUNDS;
  const D = TITLE_SIDE_IMAGE_DEFAULTS;
  const size =
    clampPx(fields.sideImageSize, B.sideImageSize) ?? D.sideImageSize;
  const radius = clampPx(fields.sideImageRadius, B.sideImageRadius) ?? size / 2;
  const gap = clampPx(fields.sideImageGap, B.sideImageGap) ?? D.sideImageGap;
  const top = fields.sideImageAlign === 'top';
  return {
    src,
    row: {
      display: 'inline-flex',
      alignItems: top ? 'flex-start' : 'center',
      gap,
      maxWidth: '100%',
      verticalAlign: 'top',
    },
    img: {
      display: 'block',
      flex: '0 0 auto',
      width: size,
      height: size,
      objectFit: 'cover',
      borderRadius: radius,
    },
    text: { minWidth: 0, flex: '1 1 auto' },
  };
}
